import { useEffect, useState } from "react";
import { Clock, Info, Loader2, RotateCcw, Trash2 } from "lucide-react";
import { computeMetrics } from "../metrics";
import { formatPercent } from "./shared";
import type {
  BenchmarkMetrics,
  CaseRunResult,
  StoredBenchmarkRun,
  StoredCaseResult,
  StoredCustomCase,
} from "../types";

interface HistoryViewProps {
  runs: StoredBenchmarkRun[];
  onViewRun: (run: StoredBenchmarkRun) => void;
  onDeleteRun: (id: string) => void;
  onBack: () => void;
  hydrateResults: (results: StoredCaseResult[], customCases?: StoredCustomCase[]) => Promise<CaseRunResult[]>;
}

export function HistoryView({
  runs,
  onViewRun,
  onDeleteRun,
  onBack,
  hydrateResults,
}: HistoryViewProps) {
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [metricsMap, setMetricsMap] = useState<Map<string, BenchmarkMetrics>>(new Map());
  const [loadingMetrics, setLoadingMetrics] = useState(true);

  useEffect(() => {
    const currentRunIds = new Set(runs.map((r) => r.id));
    const newRuns = runs.filter((r) => !metricsMap.has(r.id));

    if (newRuns.length === 0) {
      setMetricsMap((prev) => {
        const pruned = new Map(prev);
        for (const key of pruned.keys()) {
          if (!currentRunIds.has(key)) pruned.delete(key);
        }
        return pruned;
      });
      setLoadingMetrics(false);
      return;
    }

    let cancelled = false;
    setLoadingMetrics(true);

    Promise.all(
      newRuns.map(async (run) => {
        const hydrated = await hydrateResults(run.results, run.customCases);
        return [run.id, computeMetrics(hydrated)] as const;
      }),
    ).then((entries) => {
      if (cancelled) return;
      setMetricsMap((prev) => {
        const next = new Map(prev);
        for (const [id, metrics] of entries) next.set(id, metrics);
        for (const key of next.keys()) {
          if (!currentRunIds.has(key)) next.delete(key);
        }
        return next;
      });
      setLoadingMetrics(false);
    });

    return () => { cancelled = true; };
  }, [runs, hydrateResults]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-brand-foreground">
          Evaluation History
        </h1>
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 border border-brand-border text-brand-foreground rounded-md hover:bg-brand-primary/5 transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          New Run
        </button>
      </div>

      {runs.length === 0 ? (
        <div className="text-center py-16 text-brand-muted">
          <Clock className="w-12 h-12 mx-auto mb-4 opacity-40" />
          <p className="text-lg">No evaluation runs yet</p>
          <p className="text-sm mt-1">
            Run an evaluation and it will appear here
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {runs.map((run) => {
            const metrics = metricsMap.get(run.id);
            const isCustom = !!run.customCases;
            const scoredCount = metrics ? metrics.totalCases - metrics.errorCount : 0;
            const passRate = metrics && scoredCount > 0
              ? `${metrics.passedCases}/${scoredCount}`
              : "\u2014";
            return (
              <div
                key={run.id}
                onClick={() => onViewRun(run)}
                className="flex items-center justify-between p-4 bg-brand-card border border-brand-border rounded-lg hover:bg-brand-primary/5 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-6 min-w-0">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-brand-foreground flex items-center gap-2">
                      {new Date(run.timestamp).toLocaleString()}
                      {isCustom && (
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700">
                          Custom
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-brand-muted mt-0.5">
                      {run.results.length} cases &middot; {run.maxConcurrent} concurrent &middot;
                      max {run.maxMessages} messages
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6 shrink-0">
                  {run.shouldTerminateEarly ? (
                    <div className="flex items-center gap-1.5 text-xs text-blue-600">
                      <Info className="w-3.5 h-3.5" />
                      Early provision — metrics hidden
                    </div>
                  ) : loadingMetrics ? (
                    <div className="flex items-center gap-2 text-sm text-brand-muted">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading metrics...
                    </div>
                  ) : metrics ? (
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-brand-muted">
                        Accuracy{" "}
                        <span className="text-brand-foreground font-medium">
                          {formatPercent(metrics.accuracy)}
                        </span>
                      </span>
                      <span className="text-brand-muted">
                        Precision{" "}
                        <span className="text-brand-foreground font-medium">
                          {formatPercent(metrics.precision)}
                        </span>
                      </span>
                      <span className="text-brand-muted">
                        Recall{" "}
                        <span className="text-brand-foreground font-medium">
                          {formatPercent(metrics.recall)}
                        </span>
                      </span>
                      <span className="text-brand-muted">
                        Passed{" "}
                        <span className="text-brand-foreground font-medium">
                          {passRate}
                        </span>
                      </span>
                      {metrics.errorCount > 0 && (
                        <span className="text-brand-muted">
                          Errors{" "}
                          <span className="text-red-600 font-medium">
                            {metrics.errorCount}
                          </span>
                        </span>
                      )}
                    </div>
                  ) : null}
                  {confirmDeleteId === run.id ? (
                    <div
                      className="flex items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() => {
                          onDeleteRun(run.id);
                          setConfirmDeleteId(null);
                        }}
                        className="px-2 py-1 text-xs rounded bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className="px-2 py-1 text-xs rounded bg-brand-border text-brand-muted hover:text-brand-foreground transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDeleteId(run.id);
                      }}
                      className="p-1.5 rounded hover:bg-red-50 text-brand-muted hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
